import { ArrowRight, Bot, Mic, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import PropTypes from 'prop-types';

import { Button } from '@src/components/ui/button';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

/**
 * AI Agent Banner - A callout component to highlight the AI agent for each product
 *
 * @param {string} agentName - Name of the AI agent (e.g., "GRC Agent")
 * @param {number} toolsCount - Number of tools the agent has
 * @param {string[]} exampleQueries - Example queries users can ask
 * @param {string} accentColor - The product's accent color (hex)
 * @param {string} description - Brief description of what the agent does
 */
export function AIAgentBanner({
  agentName,
  toolsCount,
  exampleQueries = [],
  accentColor = '#6366f1',
  description,
}) {
  const onNavigateToTop = useNavigateToTop();

  return (
    <section className="py-16 bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 w-48 h-48 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-purple-500 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
          >
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6 border border-white/20">
              <Bot className="w-4 h-4 mr-2" style={{ color: accentColor }} />
              <span className="text-white">AI-Powered</span>
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Meet Your
              {' '}
              <span style={{ color: accentColor }}>{agentName}</span>
            </h2>

            <p className="text-lg text-slate-300 mb-6 leading-relaxed">
              {description || `Just speak naturally and let the ${agentName} handle complex queries, automate workflows, and provide instant insights.`}
            </p>

            {/* Stats */}
            <div className="flex items-center gap-6 mb-8">
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{toolsCount}</p>
                <p className="text-sm text-slate-400">AI Tools</p>
              </div>

              <div className="w-px h-12 bg-white/20" />

              <div className="text-center">
                <p className="text-3xl font-bold text-white">24/7</p>
                <p className="text-sm text-slate-400">Available</p>
              </div>

              <div className="w-px h-12 bg-white/20" />

              <div className="text-center">
                <p className="text-3xl font-bold text-white">Voice</p>
                <p className="text-sm text-slate-400">First</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                style={{ backgroundColor: accentColor }}
                className="text-white px-6 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/platform/ai-agents')}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Learn About AI Agents
              </Button>

              <Button
                variant="outline"
                className="border-white text-white bg-white/10 hover:bg-white/20 px-6 py-3 rounded-lg transition-all"
                onClick={() => onNavigateToTop('/demo')}
              >
                <Mic className="w-4 h-4 mr-2" />
                Try Voice Demo
              </Button>
            </div>
          </motion.div>

          {/* Right side - Example queries */}
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
          >
            <div className="flex items-center gap-3 mb-6">
              <div
                style={{ backgroundColor: `${accentColor}30` }}
                className="w-12 h-12 rounded-xl flex items-center justify-center"
              >
                <Mic className="w-6 h-6" style={{ color: accentColor }} />
              </div>

              <div>
                <p className="text-white font-semibold">Just Ask...</p>
                <p className="text-slate-400 text-sm">Natural language queries</p>
              </div>
            </div>

            <div className="space-y-3">
              {exampleQueries.map((query, index) => (
                <motion.div
                  key={index}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 hover:border-slate-600 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div
                      style={{ backgroundColor: accentColor }}
                      className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    />

                    <p className="text-slate-300 text-sm italic">{query}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <button
                style={{ color: accentColor }}
                className="flex items-center text-sm font-medium transition-colors hover:opacity-80"
                onClick={() => onNavigateToTop('/platform/ai-agents')}
              >
                Explore all 10 AI agents
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

AIAgentBanner.propTypes = {
  agentName: PropTypes.string.isRequired,
  toolsCount: PropTypes.number.isRequired,
  exampleQueries: PropTypes.arrayOf(PropTypes.string),
  accentColor: PropTypes.string,
  description: PropTypes.string,
};

export default AIAgentBanner;
